/** @format */

import { AddressFormType, DPV_CODE, MyAccountErrorLogKeys } from '@shared/data-access/enums';
import { IAvsResponse, IAvsResponseV2 } from '@cart-checkout/checkout/data-access/interfaces';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';

import { Address } from '@cart-checkout/data-access/interfaces';
import { AvsService } from './avs.service';
import { CHECKOUT_ERROR_MESSAGES } from '@shared/data-access/constants';
import { Injectable } from '@angular/core';
import { MyAccountAnalyticsService } from '@shared/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class MyAccountAddressService {
	private analyticErrorLogKey: MyAccountErrorLogKeys =
		MyAccountErrorLogKeys.MYACCOUNT_ADDRESS_ERROR;

	constructor(
		private avsService: AvsService,
		private myAccountAnalyticsService: MyAccountAnalyticsService
	) {}

	/**
	 * @description
	 * AVS Service call and response handling
	 *
	 * @param addressFormValue - Submitted form values
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @param shouldUseV2 - Determines how we should handle AVS response
	 */
	public avsServiceCall(
		addressFormValue: Address,
		formName: AddressFormType,
		shouldUseV2 = false
	): Observable<void | IAvsResponse | IAvsResponseV2> {
		const serviceCall$: Observable<IAvsResponse | IAvsResponseV2> = shouldUseV2
			? this.avsService.verifyAddressV2$(addressFormValue, null, formName)
			: this.avsService.verifyAddress$(addressFormValue, null, formName);

		return serviceCall$.pipe(
			concatMap((avsAddress: IAvsResponse | IAvsResponseV2) => {
				// Do the AVS actions with the AVS Response
				return this.doAvsActions(avsAddress, formName, shouldUseV2);
			}),
			catchError(
				(error: {
					dpvCode?: string;
					errorMessage?: string;
					message: string;
					name: string;
					rejectedFormField?: string;
				}) => {
					/*
					 * ***** SINGLE CATCH BLOCK FOR ERROR HANDLING *****
					 * AVS call failed or #doAvsActions returned an error (runtime error or INTENTIONAL string error in case of AVS_DOWN)
					 *
					 * NOTE: #doAvsDownActions method should NOT / will NOT log the error, error logging is handled here
					 * because there may have been some other issue that occurred (e.g. runtime error). However, error messaging displayed to
					 * the user IS handled within the #doAvsDownActions method
					 */

					// Log the error
					this.myAccountAnalyticsService.generateLogError(
						this.analyticErrorLogKey,
						this.determineAvsFailureMessageForLogging(error.message, formName),
						error
					);

					// If #doAvsActions returned an INTENTIONAL string error, it would be typeof string so this indicates
					// an XHR failure or runtime error and the #doAvsActions logic might not be completely executed
					if (typeof error !== 'string' && error.message !== 'AVS_REJECT') {
						// Treat the XHR/runtime error scenario as if the AVS service call failed and AVS is down
						this.doAvsDownActions();
					}

					// Return the error which could be an XHR failure, runtime error, or AVS action error (AVS_REJECT / AVS_DOWN)
					return throwError(error);
				}
			)
		);
	}

	/**
	 * @description
	 * Takes a zipcode string. If the zipcode is nine digits, it returns the zipcode with a dash inserted. Otherwise it returns the zipcode.
	 *
	 * @param zip - Zipcode
	 * @returns - Zipcode string that is either the input zipcode with a dash inserted or the input zipcode unchanged.
	 */
	public addDashToLongZip(zip: string): string {
		if (zip.length === 9 && zip.indexOf('-') === -1) {
			return `${zip.slice(0, 5)}-${zip.slice(5)}`;
		} else {
			return zip;
		}
	}

	/**
	 * @description
	 * Does certain actions based on the AVS result decision (e.g. ACCEPT, REJECT, etc.).
	 * Uses DPV codes to decide on a more specific action when shouldUseV2 is true.
	 *
	 * @param avsAddress - Response from AVS V1 or V2 API
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @param shouldUseV2 - Determines which reponse we should handle
	 * @returns Observable<void | IAvsResponse | IAvsResponseV2>
	 * @private
	 */
	private doAvsActions(
		avsAddress: IAvsResponse | IAvsResponseV2,
		formName: AddressFormType,
		shouldUseV2 = false
	): Observable<void | IAvsResponse | IAvsResponseV2> {
		// V2 uses DPV codes to handle more specific validation situations
		if (shouldUseV2) {
			const v2Response = avsAddress as IAvsResponseV2;
			const dpvCode: number = v2Response?.avsResult?.dpvCode;

			if (dpvCode === undefined) {
				return this.doAvsDownActions();
			}

			if (dpvCode === DPV_CODE.PERFECT_ADDRESS) {
				return this.doAvsAcceptActions(v2Response);
			} else {
				return this.doAvsRejectActions(formName, dpvCode);
			}
		}

		// V1 validation and rules
		// Check the AVS result decision
		if (avsAddress?.avsResult?.decision === 'ACCEPT') {
			return this.doAvsAcceptActions(avsAddress);
		} else if (avsAddress?.avsResult?.decision === 'REJECT') {
			// REJECT actions
			return this.doAvsRejectActions(formName);
		} else {
			// Everything broke and the world is on fire
			return this.doAvsDownActions();
		}
	}

	/**
	 * @description
	 * Executed when the AVS service responds with ACCEPT.
	 * @param avsResponse - Object returned from AVS V1 or V2 API
	 * @returns Observable<IAvsResponse | IAvsResponseV2>
	 * @private
	 */
	private doAvsAcceptActions(
		avsResponse: IAvsResponse | IAvsResponseV2
	): Observable<IAvsResponse | IAvsResponseV2> {
		return of(avsResponse);
	}

	/**
	 * @description
	 * Executed when the AVS service responds with REJECT.
	 *
	 * NOTE: This method will NOT log an error message, that is handled within #doAvsActions. This method
	 * is only responsible for returning what type of error occurred and the error message displayed
	 * to the user
	 *
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @param dpvCode - Code returned from AVS service that informs us what went wrong
	 * @returns Observable<void>
	 * @private
	 */
	private doAvsRejectActions(formName: AddressFormType, dpvCode?: number): Observable<void> {
		// Make error mesaage display dynamically based on dpv codes
		let errorMessage = '';
		switch (dpvCode) {
			case DPV_CODE.NO_DPV:
			case DPV_CODE.INVALID_ADDRESS:
				errorMessage = CHECKOUT_ERROR_MESSAGES.avsRejectedDPVCode0And2;
				break;

			case DPV_CODE.INVALID_APT_NUMBER:
				errorMessage = CHECKOUT_ERROR_MESSAGES.avsRejectedDPVCode3;
				break;

			case DPV_CODE.MISSING_APT_NUMBER:
				errorMessage = CHECKOUT_ERROR_MESSAGES.avsRejectedDPVCode4;
				break;

			default:
				errorMessage = CHECKOUT_ERROR_MESSAGES.avsSavedRejected;
		}

		if (dpvCode !== undefined) {
			this.myAccountAnalyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
				event: 'AvsV2:RejectionMessageDisplayed',
				value: `DPV Code: ${dpvCode}, ${errorMessage}`
			});
		}

		// Return an error
		return throwError({
			name: `${formName}_AVS`,
			message: 'AVS_REJECT',
			errorMessage,
			dpvCode
		});
	}

	/**
	 * @description
	 * Executed when the AVS service does NOT respond with ACCEPT or REJECT or there is a runtime error performing AVS actions
	 *
	 * NOTE: This method will NOT log an error message, that is handled within #doAvsActions. This method
	 * is only responsible for returning what type of error occurred and the error message displayed
	 * to the user
	 *
	 * @private
	 */
	private doAvsDownActions(): Observable<void> {
		// Return an error
		return throwError('AVS_DOWN');
	}

	private determineAvsFailureMessageForLogging(error: string, formType: string): string {
		if (error === 'AVS_REJECT') {
			return `AVS rejected the ${formType} address`;
		} else if (error === 'AVS_DOWN') {
			return `An AVS error occurred for the ${formType} address`;
		} else {
			return `A runtime or XHR error occurred for the ${formType} address: Error: ${error}`;
		}
	}
}
